import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Button,
} from "@mui/material";

/**
 * Phase 6.4 — log a manual savings event against a subscription.
 * Posts to /admin/workspaces/:work_id/savings-events (created_by=admin).
 * The amount rolls into the customer's dashboard "Money saved" number.
 */

const SOURCES = [
  { value: "price_negotiation", label: "Price negotiation" },
  { value: "renewal_negotiation", label: "Renewal negotiation" },
  { value: "tier_downgrade", label: "Tier downgrade" },
  { value: "vendor_swap", label: "Vendor swap" },
  { value: "license_revoke", label: "License revoke" },
];

export default function AddSavingsModal({ open, onClose, subscription }) {
  const [source, setSource] = useState("price_negotiation");
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState(
    String(subscription?.currency || "INR").toUpperCase(),
  );
  const [occurredAt, setOccurredAt] = useState(
    new Date().toISOString().slice(0, 10),
  );
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const workId = subscription?.work_id;

  const submit = async () => {
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt <= 0) {
      setMsg({ type: "error", text: "Enter an amount greater than 0." });
      return;
    }
    if (!workId) {
      setMsg({ type: "error", text: "Missing workspace id on this subscription." });
      return;
    }
    setSaving(true);
    setMsg(null);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/admin/workspaces/${workId}/savings-events`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          credentials: "include",
          body: JSON.stringify({
            source,
            amount_saved: amt,
            currency,
            occurred_at: occurredAt,
            note,
            source_subscription_id: subscription?.id || null,
          }),
        },
      );
      const json = await res.json().catch(() => ({}));
      if (res.ok && json?.ok) {
        setMsg({ type: "success", text: "Savings logged." });
        setAmount("");
        setNote("");
        setTimeout(() => {
          setMsg(null);
          onClose?.(true);
        }, 900);
      } else {
        setMsg({ type: "error", text: json?.msg || "Failed to log savings." });
      }
    } catch (e) {
      setMsg({ type: "error", text: e?.message || "Network error." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => onClose?.(false)} maxWidth="xs" fullWidth>
      <DialogTitle>
        Add savings
        {subscription?.marketplaceName ? ` — ${subscription.marketplaceName}` : ""}
      </DialogTitle>
      <DialogContent>
        <TextField
          select
          fullWidth
          margin="dense"
          label="Type"
          value={source}
          onChange={(e) => setSource(e.target.value)}
        >
          {SOURCES.map((s) => (
            <MenuItem key={s.value} value={s.value}>
              {s.label}
            </MenuItem>
          ))}
        </TextField>
        <div style={{ display: "flex", gap: 8 }}>
          <TextField
            margin="dense"
            label="Amount saved"
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            style={{ flex: 1 }}
          />
          <TextField
            select
            margin="dense"
            label="Currency"
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            style={{ width: 110 }}
          >
            <MenuItem value="INR">INR</MenuItem>
            <MenuItem value="USD">USD</MenuItem>
          </TextField>
        </div>
        <TextField
          margin="dense"
          label="Date"
          type="date"
          fullWidth
          value={occurredAt}
          onChange={(e) => setOccurredAt(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          margin="dense"
          label="Note (optional)"
          fullWidth
          multiline
          minRows={2}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        {msg && (
          <div
            style={{
              marginTop: 8,
              fontSize: 13,
              color: msg.type === "error" ? "#b91c1c" : "#047857",
            }}
          >
            {msg.text}
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose?.(false)} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={submit} variant="contained" disabled={saving}>
          {saving ? "Saving…" : "Add savings"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
